// Modules
import { UseFormSetError } from 'react-hook-form'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

// Application
import { Errors } from 'redesign/constants'
import { useSnackbar } from 'redesign/hooks/useSnackbar'
import { ROUTES_PATH } from 'constants/index'
import { useLoginContext } from 'context'
import { useLoginService } from 'services/useLoginService'
import { convertToNumber, singleSPA } from 'utils'
import { useTrackEventError } from 'redesign/hooks/useTrackEventError'
import { SignInCredentials, LoginType } from 'typings/auth'
import { useLoginSelfService } from 'redesign/store'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'

export type FormData = {
    cpf: string
    password: string
}

export const useSelfServiceCPF = () => {
    const service = useLoginService()
    const { isFeatureEnabled } = useRemoteConfig()
    const { operations } = useLoginContext()
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const { executeRecaptcha = () => '' } = useGoogleReCaptcha()
    const { handleTrackEventError } = useTrackEventError()
    const { setPage, page } = useLoginSelfService()
    const { openErrorSnackbarRetry } = useSnackbar()

    const authenticate = async (credentials: SignInCredentials): Promise<void> => {
        const data = await service.authenticate(credentials)

        if (data) {
            operations.addPayloadTokenLS({
                token: data.accessToken,
                refreshToken: data.refreshToken,
                limitedFlow: true,
                selfLogin: true,
            })
            operations.setIdentifier(LoginType.empty)
        }
    }

    const handleAuthUser = async ({
        cpf,
        password,
        setError,
    }: {
        cpf: string
        password: string
        setError: UseFormSetError<FormData>
    }) => {
        try {
            const recaptchaToken = await executeRecaptcha('login')
            const payload = {
                username: convertToNumber(cpf),
                password,
                recaptchaToken: recaptchaToken,
            }

            await authenticate(payload)
            if (newRedirectFlow) {
                return singleSPA.historyPush(`${ROUTES_PATH.FORWARDING_APP}?redirectTo=${ROUTES_PATH.EXAMS_SELF_SERVICE}`)
            }
            return singleSPA.historyPush(ROUTES_PATH.EXAMS_SELF_SERVICE)
        } catch (error) {
            const codeError = error?.graphQLErrors?.[0].extensions?.code

            handleTrackEventError({
                codeError: codeError || error.networkError?.name || error.response?.toString() || '',
                message: `${error.graphQLErrors[0]?.message || error.networkError?.message} - AuthenticateCPF`,
                accessType: 'CPF',
                access: 'autoatendimento',
            })

            if (['UNAUTHORIZED', 'CIPWITHOUTACCOUNT'].includes(codeError)) {
                setError('cpf', {}, { shouldFocus: false })
                return setError('password', { type: 'custom', message: Errors.selfService.CIPWITHOUTACCOUNT }, { shouldFocus: false })
            }

            openErrorSnackbarRetry({
                message: Errors.selfService.DEFAULT,
                onClick: () => handleAuthUser({ cpf, password, setError }),
            })
        }
    }

    function changePage() {
        setPage(page === 'cpf' ? 'cip' : 'cpf')
    }

    return {
        handleAuthUser,
        changePage,
    }
}
